import React, { useState, useMemo } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, Select, Popconfirm, Typography, Row, Col, Avatar, Descriptions, Spin, Collapse, InputNumber } from 'antd';
import { TeamOutlined, DeleteOutlined, EyeOutlined, EditOutlined, UserOutlined, ProfileOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { companyProfileQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import apiService from '../../../services/api';
import { CandidatePoolResponse, UpdatePoolStatusRequest, CandidateFilterRequest } from '../../../types';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const cleanFilters = (f: CandidateFilterRequest): Partial<CandidateFilterRequest> => {
  const cleaned: Partial<CandidateFilterRequest> = {};
  Object.keys(f).forEach(key => {
    const value = f[key as keyof CandidateFilterRequest];
    if (value !== undefined && value !== null && value !== '') (cleaned as any)[key] = value;
  });
  return cleaned;
};

const PoolTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidatePoolResponse | null>(null);
  const [form] = Form.useForm();
  const [candidateDetailModalVisible, setCandidateDetailModalVisible] = useState(false);
  const [candidateDetailLoading, setCandidateDetailLoading] = useState(false);
  const [candidateDetail, setCandidateDetail] = useState<any>(null);
  const [advancedFiltersVisible, setAdvancedFiltersVisible] = useState(false);
  const [filters, setFilters] = useState<CandidateFilterRequest>({});

  const statusOptions = [
    { value: 'Available', label: 'Müsait', color: 'blue' },
    { value: 'Contacted', label: 'İletişim Kuruldu', color: 'orange' },
    { value: 'Interviewed', label: 'Mülakat Yapıldı', color: 'purple' },
    { value: 'Hired', label: 'İşe Alındı', color: 'green' },
    { value: 'Rejected', label: 'Reddedildi', color: 'red' }
  ];

  const filterParams = useMemo(() => {
    const cleaned = cleanFilters(filters);
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }, [filters]);

  const { data: pool = [], isLoading: loading } = useQuery({
    queryKey: companyProfileQueryKeys.pool(filterParams ?? {}),
    queryFn: async () => {
      const r = await apiService.getCandidatePool(filterParams);
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: mentors = [] } = useQuery({
    queryKey: companyProfileQueryKeys.mentors,
    queryFn: async () => {
      const r = await apiService.searchMentors({ page: 1, pageSize: 100 });
      if (!r.success) throw new Error(r.message);
      return r.data?.data ?? [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const updatePoolMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePoolStatusRequest }) => apiService.updatePoolStatus(id, data),
    onSuccess: (response) => {
      if (response.success) {
        setEditModalVisible(false);
        form.resetFields();
        setSelectedCandidate(null);
        queryClient.invalidateQueries({ queryKey: ['company-panel', 'pool'] });
        toast.success('Durum güncellendi');
      } else toast.error(response.message || 'Güncelleme sırasında hata oluştu');
    },
    onError: () => toast.error('Güncelleme sırasında hata oluştu'),
  });

  const removePoolMutation = useMutation({
    mutationFn: (id: number) => apiService.removeFromPool(id),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Aday havuzdan çıkarıldı');
        queryClient.invalidateQueries({ queryKey: ['company-panel', 'pool'] });
      } else toast.error(response.message || 'Havuzdan çıkarılırken hata oluştu');
    },
    onError: () => toast.error('Havuzdan çıkarılırken hata oluştu'),
  });

  const handleFilterChange = (key: keyof CandidateFilterRequest, value: any) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (value === undefined || value === null || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      return newFilters;
    });
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleEdit = (candidate: CandidatePoolResponse) => {
    setSelectedCandidate(candidate);
    form.setFieldsValue({
      status: candidate.status,
      notes: candidate.notes
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedCandidate) return;
      const updateData: UpdatePoolStatusRequest = { status: values.status, notes: values.notes };
      updatePoolMutation.mutate({ id: selectedCandidate.id, data: updateData });
    } catch (error) {
      toast.error('Güncelleme sırasında hata oluştu');
    }
  };

  const handleRemove = (id: number) => {
    removePoolMutation.mutate(id);
  };

  const handleViewCandidateDetail = async (candidate: CandidatePoolResponse) => {
    setCandidateDetail(candidate);
    setCandidateDetailLoading(true);
    setCandidateDetailModalVisible(true);
    
    try {
      if (candidate.applicantType === 'Mentee' && candidate.menteeId) {
        // Temel bilgileri yükle
        const response = await apiService.getMenteeDetail(candidate.menteeId);
        if (response.success) {
          let detail: any = { ...response.data };
          
          // Backend field mapping - MenteeDetailResponse field'larını frontend formatına çevir
          detail.phoneNumber = response.data.phone;
          detail.city = (response.data as any).location || (response.data as any).city || null;
          detail.websiteUrl = response.data.website;
          detail.linkedInProfile = response.data.linkedIn;
          detail.githubProfile = response.data.github;
          detail.title = response.data.title || null;
          detail.hourlyRate = null; // Mentee'de yok
          
          // Detaylı bilgileri ayrı ayrı yükle
          try {
            const [skillsRes, educationsRes, workExpRes, projectsRes, certificatesRes] = await Promise.all([
              apiService.getMenteeSkillsById(candidate.menteeId),
              apiService.getMenteeEducationsById(candidate.menteeId),
              apiService.getMenteeWorkExperiencesById(candidate.menteeId),
              apiService.getMenteeProjectsById(candidate.menteeId),
              apiService.getMenteeCertificatesById(candidate.menteeId)
            ]);
            
            if (skillsRes.success && skillsRes.data) detail.skills = skillsRes.data;
            if (educationsRes.success && educationsRes.data) detail.educations = educationsRes.data;
            if (workExpRes.success && workExpRes.data) detail.workExperiences = workExpRes.data;
            if (projectsRes.success && projectsRes.data) detail.projects = projectsRes.data;
            if (certificatesRes.success && certificatesRes.data) detail.certificates = certificatesRes.data;
          } catch (err) {
            console.error('Error loading mentee detail sections:', err);
          }
          
          setCandidateDetail({ ...candidate, detail });
        } else {
          toast.error('Mentee detayları yüklenemedi');
        }
      } else if (candidate.applicantType === 'Mentor' && candidate.mentorId) {
        // Temel bilgileri yükle
        const response = await apiService.getMentorDetail(candidate.mentorId);
        if (response.success) {
          let detail: any = { ...response.data };
          
          // Backend field mapping - MentorDetailResponse field'larını frontend formatına çevir
          detail.phoneNumber = response.data.phone;
          detail.city = (response.data as any).location || (response.data as any).city || null;
          detail.websiteUrl = response.data.website;
          detail.linkedInProfile = response.data.linkedIn;
          detail.githubProfile = response.data.github;
          detail.hourlyRate = response.data.hourlyRate;
          
          // Detaylı bilgileri ayrı ayrı yükle
          try {
            const [skillsRes, educationsRes, workExpRes, projectsRes, certificatesRes] = await Promise.all([
              apiService.getMentorSkills(candidate.mentorId),
              apiService.getMentorEducations(candidate.mentorId),
              apiService.getMentorWorkExperiences(candidate.mentorId),
              apiService.getMentorProjects(candidate.mentorId),
              apiService.getMentorCertificates(candidate.mentorId)
            ]);
            
            if (skillsRes.success && skillsRes.data) detail.skills = skillsRes.data;
            if (educationsRes.success && educationsRes.data) detail.educations = educationsRes.data;
            if (workExpRes.success && workExpRes.data) detail.workExperiences = workExpRes.data;
            if (projectsRes.success && projectsRes.data) detail.projects = projectsRes.data;
            if (certificatesRes.success && certificatesRes.data) detail.certificates = certificatesRes.data;
          } catch (err) {
            console.error('Error loading mentor detail sections:', err);
          }
          
          setCandidateDetail({ ...candidate, detail });
        } else {
          toast.error('Mentor detayları yüklenemedi');
        }
      } else {
        toast.error('Aday ID\'si bulunamadı');
      }
    } catch (error) {
      console.error('Error loading candidate detail:', error);
      toast.error('Aday detayları yüklenirken hata oluştu');
    } finally {
      setCandidateDetailLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusConfig = statusOptions.find(s => s.value === status);
    return statusConfig?.color || 'default';
  };

  const getStatusLabel = (status: string) => {
    const statusConfig = statusOptions.find(s => s.value === status);
    return statusConfig?.label || status;
  };

  const columns = [
    {
      title: 'Aday',
      key: 'applicant',
      width: 200,
      render: (_: any, record: CandidatePoolResponse) => (
        <Space>
          <Avatar 
            style={{ 
              backgroundColor: record.applicantType === 'Mentee' ? 'var(--primary-500)' : 'var(--success)' 
            }}
          >
            {record.applicantName.charAt(0)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{record.applicantName}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>{record.applicantEmail}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Tür',
      dataIndex: 'applicantType',
      key: 'applicantType',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'Mentee' ? 'blue' : 'green'}>
          {type === 'Mentee' ? 'Mentee' : 'Mentor'}
        </Tag>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'Notlar',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      width: 250,
    },
    {
      title: 'Eklenme Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: CandidatePoolResponse) => (
        <Space wrap>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewCandidateDetail(record)}
          >
            Detay
          </Button>
          <Button
            size="small"
            icon={<ProfileOutlined />}
            onClick={() => {
              if (record.applicantType === 'Mentee' && record.menteeId) {
                window.open(`/mentee/${record.menteeId}`, '_blank');
              } else if (record.applicantType === 'Mentor' && record.mentorId) {
                window.open(`/mentor/${record.mentorId}`, '_blank');
              }
            }}
          >
            Profil Görüntüle
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Bu adayı havuzdan çıkar"
            description="Emin misiniz?"
            onConfirm={() => handleRemove(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              Çıkar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="panel-tab-content">
      <TabListLoading loading={loading}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <TeamOutlined style={{ marginRight: '8px', color: 'var(--primary-500)' }} />
              Aday Havuzu
            </Title>
            <Text type="secondary">
              Potansiyel adaylarınızı burada yönetebilirsiniz
            </Text>
          </Col>
        </Row>

        {/* eslint-disable react/jsx-no-duplicate-props */}
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={pool}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} / ${total} aday`,
          }}
        />
        {/* eslint-enable react/jsx-no-duplicate-props */}
      </Card>
      </TabListLoading>

      {/* Edit Status Modal */}
      <Modal
        title="Durum ve Notları Düzenle"
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
          setSelectedCandidate(null);
        }}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="status"
            label="Durum"
            rules={[{ required: true, message: 'Durum seçiniz' }]}
          >
            <Select placeholder="Durum seçiniz">
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="notes"
            label="Notlar"
          >
            <TextArea
              rows={4}
              placeholder="Aday hakkında notlarınızı yazın..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Candidate Detail Modal */}
      <Modal
        title={`${candidateDetail?.applicantName || 'Aday'} - Detayları`}
        open={candidateDetailModalVisible}
        onCancel={() => {
          setCandidateDetailModalVisible(false);
          setCandidateDetail(null);
        }}
        width={1000}
        footer={[
          <Button key="close" onClick={() => {
            setCandidateDetailModalVisible(false);
            setCandidateDetail(null);
          }}>
            Kapat
          </Button>
        ]}
      >
        {candidateDetailLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : candidateDetail?.detail ? (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Kişisel Bilgiler" size="small">
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Text strong>Ad Soyad:</Text> {candidateDetail.detail.firstName} {candidateDetail.detail.lastName}
                    </Col>
                    <Col span={12}>
                      <Text strong>E-posta:</Text> {candidateDetail.detail.email}
                    </Col>
                    <Col span={12}>
                      <Text strong>Telefon:</Text> {candidateDetail.detail.phoneNumber || 'Belirtilmemiş'}
                    </Col>
                    <Col span={12}>
                      <Text strong>Şehir:</Text> {candidateDetail.detail.city || 'Belirtilmemiş'}
                    </Col>
                    {candidateDetail.applicantType === 'Mentor' && candidateDetail.detail.title && (
                      <Col span={12}>
                        <Text strong>Ünvan:</Text> {candidateDetail.detail.title}
                      </Col>
                    )}
                    {candidateDetail.applicantType === 'Mentor' && candidateDetail.detail.hourlyRate && (
                      <Col span={12}>
                        <Text strong>Saatlik Ücret:</Text> ₺{candidateDetail.detail.hourlyRate}
                      </Col>
                    )}
                    <Col span={24}>
                      <Text strong>Bio:</Text> {candidateDetail.detail.bio || 'Belirtilmemiş'}
                    </Col>
                  </Row>
                </Card>
              </Col>
              
              {(candidateDetail.detail.websiteUrl || candidateDetail.detail.linkedInProfile || candidateDetail.detail.githubProfile) && (
                <Col span={24}>
                  <Card title="İletişim Bilgileri" size="small">
                    <Descriptions column={2} bordered>
                      {candidateDetail.detail.websiteUrl && (
                        <Descriptions.Item label="Website" span={2}>
                          <a href={candidateDetail.detail.websiteUrl} target="_blank" rel="noopener noreferrer">
                            {candidateDetail.detail.websiteUrl}
                          </a>
                        </Descriptions.Item>
                      )}
                      {candidateDetail.detail.linkedInProfile && (
                        <Descriptions.Item label="LinkedIn" span={2}>
                          <a href={candidateDetail.detail.linkedInProfile} target="_blank" rel="noopener noreferrer">
                            {candidateDetail.detail.linkedInProfile}
                          </a>
                        </Descriptions.Item>
                      )}
                      {candidateDetail.detail.githubProfile && (
                        <Descriptions.Item label="GitHub" span={2}>
                          <a href={candidateDetail.detail.githubProfile} target="_blank" rel="noopener noreferrer">
                            {candidateDetail.detail.githubProfile}
                          </a>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                </Col>
              )}

              {candidateDetail.detail.skills && candidateDetail.detail.skills.length > 0 && (
                <Col span={24}>
                  <Card title="Yetenekler" size="small">
                    <Space wrap>
                      {candidateDetail.detail.skills.map((skill: any, index: number) => (
                        <Tag key={index} color="blue">{skill.skillName}</Tag>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}

              {candidateDetail.detail.workExperiences && candidateDetail.detail.workExperiences.length > 0 && (
                <Col span={24}>
                  <Card title="İş Deneyimi" size="small">
                    {candidateDetail.detail.workExperiences.map((work: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{work.companyName}</Text> - {work.position}
                        <br />
                        <Text type="secondary">{dayjs(work.startDate).format('MMM YYYY')} - {(work.isCurrentJob || !work.endDate) ? 'Günümüz' : dayjs(work.endDate).format('MMM YYYY')}</Text>
                        {work.description && <div style={{ marginTop: '4px' }}>{work.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {candidateDetail.detail.educations && candidateDetail.detail.educations.length > 0 && (
                <Col span={24}>
                  <Card title="Eğitim Bilgileri" size="small">
                    {candidateDetail.detail.educations.map((edu: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{edu.schoolName}</Text> - {edu.degree || edu.department}
                        <br />
                        <Text type="secondary">{(edu.department || edu.fieldOfStudy || '')} • {dayjs(edu.startDate).format('YYYY')} - {edu.endDate ? dayjs(edu.endDate).format('YYYY') : (edu.isGraduated ? '-' : 'Günümüz')}</Text>
                        {edu.description && <div style={{ marginTop: '4px' }}>{edu.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {candidateDetail.detail.projects && candidateDetail.detail.projects.length > 0 && (
                <Col span={24}>
                  <Card title="Projeler" size="small">
                    {candidateDetail.detail.projects.map((project: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{project.projectName}</Text>
                        <br />
                        <Text type="secondary">{dayjs(project.startDate).format('MMM YYYY')} - {project.endDate ? dayjs(project.endDate).format('MMM YYYY') : 'Devam ediyor'}</Text>
                        {project.projectDescription && <div style={{ marginTop: '4px' }}>{project.projectDescription}</div>}
                        {project.technologies && (
                          <div style={{ marginTop: '4px' }}>
                            <Space wrap>
                              {(Array.isArray(project.technologies) ? project.technologies : project.technologies.split(',')).map((tech: string, techIndex: number) => (
                                <Tag key={techIndex} color="green">{tech.trim()}</Tag>
                              ))}
                            </Space>
                          </div>
                        )}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {candidateDetail.detail.certificates && candidateDetail.detail.certificates.length > 0 && (
                <Col span={24}>
                  <Card title="Sertifikalar" size="small">
                    {candidateDetail.detail.certificates.map((cert: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{cert.certificateName}</Text>
                        <br />
                        <Text type="secondary">{cert.issuingOrganization} • {dayjs(cert.issueDate).format('MMM YYYY')}</Text>
                        {cert.description && <div style={{ marginTop: '4px' }}>{cert.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {candidateDetail.applicantType === 'Mentor' && candidateDetail.detail.favouriteMentees && candidateDetail.detail.favouriteMentees.length > 0 && (
                <Col span={24}>
                  <Card title="Referans verdiği menteeler" size="small">
                    <Space wrap>
                      {candidateDetail.detail.favouriteMentees.map((m: { id: number; firstName: string; lastName: string }) => (
                        <Button key={m.id} type="link" size="small" onClick={() => window.open(`/mentee/${m.id}`, '_blank')}>
                          {m.firstName} {m.lastName}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
              {candidateDetail.applicantType === 'Mentee' && candidateDetail.detail.referencedByMentors && candidateDetail.detail.referencedByMentors.length > 0 && (
                <Col span={24}>
                  <Card title="Referansları" size="small">
                    <Space wrap>
                      {candidateDetail.detail.referencedByMentors.map((m: { id: number; firstName: string; lastName: string }) => (
                        <Button key={m.id} type="link" size="small" onClick={() => window.open(`/mentor/${m.id}`, '_blank')}>
                          {m.firstName} {m.lastName}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Text>Aday detayları yüklenemedi</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PoolTab;
